import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { CodeDefinition } from '../../../js/api';
import { CodeDefinitionsService } from '../../../js/api';

/**
 * CodeDefinitions Manager
 *
 * This module provides a centralized way to fetch and access code definitions across the application.
 * Code definitions are reference data (e.g., US states, specialties, board certifications) that rarely change.
 *
 * **Architecture:**
 * - Fetches ALL code definitions in a single API call on first use
 * - Caches data indefinitely using React Query (staleTime: Infinity)
 * - All components share the same cached data (no duplicate API calls)
 * - Data is organized by category for efficient lookups
 *
 * **Usage Pattern:**
 * ```typescript
 * // Get as dropdown options
 * const { data: specialties, isLoading } = useCodeDefinitionsAsOptions('Specialty');
 *
 * // Get as code-to-title map
 * const { data: stateMap } = useCodeDefinitionsAsMap('State');
 *
 * // Get multiple categories as maps
 * const { data: maps } = useCodeDefinitionsAsMaps(['Degree', 'CertificationType']);
 * ```
 *
 * **Performance:**
 * - First component to call any hook triggers the API fetch
 * - All subsequent calls use cached data (instant, no API call)
 * - Data persists across component mounts/unmounts
 * - Cache survives for 24 hours (gcTime)
 */

/**
 * Main hook that fetches and caches all code definitions.
 *
 * This hook makes a single API call to fetch ALL code definitions and transforms
 * them into an efficient lookup structure organized by category.
 *
 * **Note:** You typically don't need to call this hook directly. Use the specialized
 * hooks below (useCodeDefinitionsAsOptions, useCodeDefinitionsAsMap, etc.) instead.
 *
 * @returns Query result containing all code definitions organized by category
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useCodeDefinitions();
 * // data.all - Array of all code definitions
 * // data.byCategory - Map<string, CodeDefinition[]> grouped by category
 * ```
 */
export const useCodeDefinitions = () => {
  return useQuery({
    queryKey: ['codeDefinitions', 'all'],
    queryFn: async () => {
      const definitions = await CodeDefinitionsService.codeDefinitionsList({
        ordering: 'sequence',
      });

      // Transform into a map by category for efficient lookups
      const byCategory = new Map<string, CodeDefinition[]>();
      definitions.forEach((def) => {
        if (!byCategory.has(def.category)) {
          byCategory.set(def.category, []);
        }
        byCategory.get(def.category)!.push(def);
      });

      return {
        all: definitions,
        byCategory,
      };
    },
    staleTime: Infinity, // Code defs rarely change
    cacheTime: 24 * 60 * 60 * 1000, // cache for 24 hours
  });
};

/**
 * Get code definitions for a specific category.
 *
 * Returns the raw CodeDefinition objects for a single category.
 *
 * @param category - The category to retrieve (e.g., 'Specialty', 'Degree', 'State')
 * @returns Query result with array of CodeDefinition objects
 *
 * @example
 * ```typescript
 * const { data: specialties, isLoading, error } = useCodeDefinitionsByCategory('Specialty');
 * // data = [{ code: 'CARD', title: 'Cardiology', category: 'Specialty', ... }, ...]
 * ```
 */
export const useCodeDefinitionsByCategory = (category: string) => {
  const { data, isLoading, error } = useCodeDefinitions();

  const categoryData = useMemo(() => data?.byCategory.get(category) || [], [data, category]);

  return {
    data: categoryData,
    isLoading,
    error,
  };
};

/**
 * Get code definitions for multiple categories.
 *
 * Returns a record mapping category names to their CodeDefinition arrays.
 *
 * @param categories - Array of category names to retrieve
 * @returns Query result with record of category → CodeDefinition[] mappings
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useCodeDefinitionsByCategories(['Specialty', 'Degree']);
 * // data = {
 * //   Specialty: [{ code: 'CARD', title: 'Cardiology', ... }],
 * //   Degree: [{ code: 'MD', title: 'Doctor of Medicine', ... }]
 * // }
 * ```
 */
export const useCodeDefinitionsByCategories = (categories: string[]) => {
  const { data, isLoading, error } = useCodeDefinitions();

  const categoriesData = useMemo(() => {
    const result: Record<string, CodeDefinition[]> = {};
    categories.forEach((category) => {
      result[category] = data?.byCategory.get(category) || [];
    });
    return result;
  }, [data, categories]);

  return {
    data: categoriesData,
    isLoading,
    error,
  };
};

/**
 * Get code definitions formatted as dropdown/select options.
 *
 * Transforms code definitions into {value, label} format commonly used by form libraries
 * and UI component libraries (React Select, Headless UI, etc.).
 *
 * @param category - The category to retrieve
 * @returns Query result with array of {value: code, label: title} objects
 *
 * @example
 * ```typescript
 * const { data: specialtyOptions, isLoading } = useCodeDefinitionsAsOptions('Specialty');
 * // data = [
 * //   { value: 'CARD', label: 'Cardiology' },
 * //   { value: 'DERM', label: 'Dermatology' },
 * //   ...
 * // ]
 * // Can be used directly with select components:
 * // <Select options={specialtyOptions} />
 * ```
 */
export const useCodeDefinitionsAsOptions = <T extends string = string>(category: string) => {
  const { data, isLoading, error } = useCodeDefinitions();

  const options = useMemo(
    () =>
      (data?.byCategory.get(category) || []).map((def) => ({
        value: def.code as T,
        label: def.title,
        is_active: def.is_active,
      })),
    [data, category]
  );

  return {
    data: options,
    isLoading,
    error,
  };
};

/**
 * Get code definitions as a code-to-title lookup map.
 *
 * Useful for displaying human-readable titles when you have a code value.
 * Common use case: rendering table cells or detail views where you have the code
 * and need to display the corresponding title.
 *
 * @param category - The category to retrieve
 * @returns Query result with object mapping code → title
 *
 * @example
 * ```typescript
 * const { data: specialtyMap } = useCodeDefinitionsAsMap('Specialty');
 * // data = { 'CARD': 'Cardiology', 'DERM': 'Dermatology', ... }
 *
 * // Usage in rendering:
 * const displayName = specialtyMap?.[provider.specialty_code] || 'Unknown';
 * ```
 */
export const useCodeDefinitionsAsMap = (category: string) => {
  const { data, isLoading, error } = useCodeDefinitions();

  const map = useMemo(() => {
    const result: Record<string, string> = {};
    (data?.byCategory.get(category) || []).forEach((def) => {
      result[def.code] = def.title;
    });
    return result;
  }, [data, category]);

  return {
    data: map,
    isLoading,
    error,
  };
};

/**
 * Get multiple categories as code-to-title lookup maps.
 *
 * Efficient way to get lookup maps for multiple categories at once.
 * Returns a nested object structure: category → code → title.
 *
 * @param categories - Array of category names to retrieve
 * @returns Query result with nested object: Record<category, Record<code, title>>
 *
 * @example
 * ```typescript
 * const { data: maps } = useCodeDefinitionsAsMaps(['Specialty', 'Degree']);
 * // data = {
 * //   Specialty: { 'CARD': 'Cardiology', 'DERM': 'Dermatology' },
 * //   Degree: { 'MD': 'Doctor of Medicine', 'DO': 'Doctor of Osteopathy' }
 * // }
 *
 * // Usage:
 * const specialtyName = maps?.Specialty[provider.specialty_code];
 * const degreeName = maps?.Degree[provider.degree_code];
 * ```
 */
export const useCodeDefinitionsAsMaps = (categories: string[]) => {
  const { data, isLoading, error } = useCodeDefinitions();

  const maps = useMemo(() => {
    const result: Record<string, Record<string, string>> = {};
    categories.forEach((category) => {
      const categoryMap: Record<string, string> = {};
      (data?.byCategory.get(category) || []).forEach((def) => {
        categoryMap[def.code] = def.title;
      });
      result[category] = categoryMap;
    });
    return result;
  }, [data, categories]);

  return {
    data: maps,
    isLoading,
    error,
  };
};
